<template>
    <b-modal
        id="modalUserCreateEdit"
        title-class="text-light"
        header-bg-variant="dark"
        header-text-variant="light"
        title-tag="h4"
        size="xl"
        centered
        @show="openModalHandler"
        @hidden="hideModal"
    >
        <template slot="modal-title">
            {{ modalTitle }}
        </template>
        <div class="row col-12 ml-0 pt-3">
            <div class="col-12 form-group">
                <label for="role">Role</label>
                <b-form-select
                    id="role"
                    v-model="data.role"
                    :disabled="editMode"
                    :options="availableRoles"
                    @input="$v.$reset"
                />
            </div>
            <div class="col-12 form-group">
                <label for="email">Email</label>
                <input
                    id="email"
                    v-model="data.email"
                    class="form-control"
                    :class="{
                        'is-invalid': $v.data.email.$error || isEmailTaken
                    }"
                    type="text"
                    @input="isEmailTaken = false"
                />
                <div v-if="isEmailTaken" class="invalid-feedback">
                    Email already taken!
                </div>
            </div>
            <div class="col-6 form-group">
                <label for="firstName">First Name</label>
                <input
                    id="firstName"
                    v-model="data.firstName"
                    class="form-control"
                    :class="{ 'is-invalid': $v.data.firstName.$error }"
                    type="text"
                    dir="auto"
                />
            </div>
            <div class="col-6 form-group">
                <label for="lastName">Last Name</label>
                <input
                    id="lastName"
                    v-model="data.lastName"
                    class="form-control"
                    :class="{ 'is-invalid': $v.data.lastName.$error }"
                    type="text"
                    dir="auto"
                />
            </div>
        </div>

        <template #modal-footer>
            <div
                class="col-12 d-flex justify-content-between pr-4 pl-4 pt-2 pb-2"
            >
                <button
                    class="btn pl-5 pr-5 btn-success waves-effect waves-light"
                    @click="submitUserDataHandler"
                >
                    Save
                </button>
                <button
                    class="btn pl-5 pr-5 btn-danger waves-effect waves-light"
                    @click="hideModal"
                >
                    Cancel
                </button>
            </div>
        </template>
    </b-modal>
</template>

<script>
import { mapActions } from 'vuex';
import { required, minLength, email } from 'vuelidate/lib/validators';

export default {
    validations: {
        data: {
            email: {
                required,
                email
            },
            role: {
                required
            },
            firstName: {
                required,
                minLength: minLength(2)
            },
            lastName: {
                required,
                minLength: minLength(2)
            }
        }
    },

    props: {
        updatingUser: {
            type: Object,
            required: false,
            default: null
        },
        availableRoles: {
            type: Array,
            required: true,
            default: () => []
        },
        selectedRole: {
            type: String,
            required: false,
            default: null
        },
        loggedUser: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            data: {},
            modalTitle: 'Add new user',
            isEmailTaken: false
        };
    },

    computed: {
        editMode() {
            return !!(this.updatingUser && this.updatingUser.id);
        }
    },

    methods: {
        ...mapActions({
            storeUser: 'users/store',
            updateUser: 'users/update'
        }),

        hideModal() {
            this.$bvModal.hide('modalUserCreateEdit');
            this.$emit('hide');
            this.data = {};
            this.$v.$reset();
        },

        async openModalHandler() {
            await this.$nextTick();

            if (this.updatingUser) {
                this.data = { ...this.updatingUser };
                this.modalTitle = `Edit ${this.data.fullName || 'User'}`;

                return;
            }

            this.modalTitle = 'Add new user';
            this.data = {
                role: this.selectedRole || null
            };
        },

        async submitUserDataHandler() {
            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            try {
                let message = 'User details has been updated!';

                if (this.editMode) {
                    const user = await this.updateUser({
                        id: this.updatingUser.id,
                        data: this.data
                    });

                    this.$emit('updated', user);
                } else {
                    const user = await this.storeUser(this.data);

                    message = 'User has been added';
                    this.$emit('stored', user);
                }

                this.$bvToast.toast(message, {
                    title: 'updated!',
                    variant: 'success',
                    solid: true
                });

                this.hideModal();
            } catch (error) {
                console.error(error);

                if (
                    error.response &&
                    error.response.status === 400 &&
                    error.response.data.errors.some(e => e.param === 'email')
                ) {
                    this.isEmailTaken = true;
                } else {
                    const message =
                        error.response &&
                        error.response.data &&
                        error.response.data.error
                            ? error.response.data.error
                            : 'Something went wrong!';

                    this.$bvToast.toast(message, {
                        title: 'Error!',
                        variant: 'danger',
                        solid: true
                    });
                }
            }
        }
    }
};
</script>

<style>
.modal-body {
    padding: 0;
}
</style>
